import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import axios from 'axios';
import { Toaster } from '@/components/ui/sonner';
import Landing from '@/pages/Landing';
import Login from '@/pages/Login';
import Register from '@/pages/Register';
import Dashboard from '@/pages/Dashboard';
import AddVehicle from '@/pages/AddVehicle';
import BookAppointment from '@/pages/BookAppointment';
import AdminLogin from '@/pages/AdminLogin';
import AdminDashboard from '@/pages/AdminDashboard';
import SecretaryDashboard from '@/pages/SecretaryDashboard';
import TechnicianDashboard from '@/pages/TechnicianDashboard';
import TechnicianRegister from '@/pages/TechnicianRegister';
import MarketerDashboard from '@/pages/MarketerDashboard';
import MarketerRegister from '@/pages/MarketerRegister';
import MerchantRegister from '@/pages/MerchantRegister';
import MerchantLogin from '@/pages/MerchantLogin';
import MerchantDashboard from '@/pages/MerchantDashboard';
import OrganizationRegister from '@/pages/OrganizationRegister';
import OrganizationLogin from '@/pages/OrganizationLogin';
import OrganizationDashboard from '@/pages/OrganizationDashboard';
import AddToHomeScreen from '@/components/AddToHomeScreen';
import { InstallProvider } from '@/context/InstallContext';
import '@/index.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const AuthContext = React.createContext(null);

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }
  }, []);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
    } catch (error) {
      console.error('Failed to fetch user:', error);
      localStorage.removeItem('token');
      delete axios.defaults.headers.common['Authorization'];
    } finally {
      setLoading(false);
    }
  };

  const login = (token, userData) => {
    localStorage.setItem('token', token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    setUser(userData);
  };

  const logout = () => {
    localStorage.removeItem('token');
    delete axios.defaults.headers.common['Authorization'];
    setUser(null);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-xl font-heading text-foreground">Loading...</div>
      </div>
    );
  }

  return (
    <InstallProvider>
      <AuthContext.Provider value={{ user, login, logout }}>
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/login" element={!user ? <Login /> : 
              user.role === 'admin' ? <Navigate to="/admin" /> :
              user.role === 'secretary' ? <Navigate to="/secretary" /> :
              user.role === 'technician' ? <Navigate to="/technician" /> :
              user.role === 'marketer' ? <Navigate to="/marketer" /> :
              <Navigate to="/dashboard" />
            } />
            <Route path="/register" element={!user ? <Register /> : 
              user.role === 'admin' ? <Navigate to="/admin" /> :
              user.role === 'secretary' ? <Navigate to="/secretary" /> :
              user.role === 'technician' ? <Navigate to="/technician" /> :
              user.role === 'marketer' ? <Navigate to="/marketer" /> :
              <Navigate to="/dashboard" />
            } />
            <Route path="/dashboard" element={user && user.role === 'user' ? <Dashboard /> : <Navigate to="/login" />} />
            <Route path="/vehicles/add" element={user ? <AddVehicle /> : <Navigate to="/login" />} />
            <Route path="/vehicles/edit/:id" element={user ? <AddVehicle /> : <Navigate to="/login" />} />
            <Route path="/appointments/book" element={user ? <BookAppointment /> : <Navigate to="/login" />} />
            <Route path="/admin/login" element={!user ? <AdminLogin /> : user.role === 'admin' ? <Navigate to="/admin" /> : <Navigate to="/dashboard" />} />
            <Route path="/admin" element={user && user.role === 'admin' ? <AdminDashboard /> : <Navigate to="/admin/login" />} />
            <Route path="/secretary" element={user && user.role === 'secretary' ? <SecretaryDashboard /> : <Navigate to="/login" />} />
            <Route path="/technician" element={user && user.role === 'technician' ? <TechnicianDashboard /> : <Navigate to="/login" />} />
            <Route path="/marketer" element={user && (user.role === 'marketer' || user.role === 'technician') ? <MarketerDashboard /> : <Navigate to="/login" />} />
            {/* Staff Registration Routes */}
            <Route path="/technician/register" element={<TechnicianRegister />} />
            <Route path="/marketer/register" element={<MarketerRegister />} />
            {/* Merchant Routes */}
            <Route path="/merchant/register" element={<MerchantRegister />} />
            <Route path="/merchant/login" element={!user || user.role !== 'merchant' ? <MerchantLogin /> : <Navigate to="/merchant" />} />
            <Route path="/merchant" element={user && user.role === 'merchant' ? <MerchantDashboard /> : <Navigate to="/merchant/login" />} />
            {/* Organization Routes */}
            <Route path="/organization/register" element={<OrganizationRegister />} />
            <Route path="/organization/login" element={!user || user.role !== 'organization' ? <OrganizationLogin /> : <Navigate to="/organization" />} />
            <Route path="/organization" element={user && user.role === 'organization' ? <OrganizationDashboard /> : <Navigate to="/organization/login" />} />
          </Routes>
        </BrowserRouter>
        <AddToHomeScreen />
        <Toaster />
      </AuthContext.Provider>
    </InstallProvider>
  );
}

export default App;